import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Search, UserCheck, CheckCircle, Home, Filter } from 'lucide-react';

export function StaffSearchPanel({
  searchMode, setSearchMode,
  idNumber, setIdNumber, handleSearchById,
  villaSearch, setVillaSearch, handleSearchByVilla,
  villaStaffList, handleSelectStaffFromVilla,
  searchedStaff, selectedVilla, setSelectedVilla,
  handleCheckIn, clearSearch, searching
}) {
  return (
    <Card className="shadow-xl border-0">
      <CardHeader className="bg-gradient-to-r from-gk-navy-light to-gk-navy text-white rounded-t-xl">
        <CardTitle className="flex items-center gap-2">
          <UserCheck className="w-5 h-5" />
          Staff Check-in System
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Search Mode Toggle */}
          <div className="flex gap-2 mb-4">
            <Button
              onClick={() => { setSearchMode('id'); clearSearch(); }}
              variant={searchMode === 'id' ? 'default' : 'outline'}
              className={searchMode === 'id' ? 'bg-gk-navy-light hover:bg-gk-navy' : ''}
            >
              <Search className="w-4 h-4 mr-2" />
              Search by ID
            </Button>
            <Button
              onClick={() => { setSearchMode('villa'); clearSearch(); }}
              variant={searchMode === 'villa' ? 'default' : 'outline'}
              className={searchMode === 'villa' ? 'bg-emerald-600' : ''}
            >
              <Home className="w-4 h-4 mr-2" />
              Search by Villa
            </Button>
          </div>

          {/* Search by ID */}
          {searchMode === 'id' && (
            <div className="space-y-2">
              <Label htmlFor="staff-id">Staff ID Number (Aadhaar/ID)</Label>
              <div className="flex gap-2">
                <Input
                  id="staff-id"
                  data-testid="staff-id-input"
                  placeholder="Enter Aadhaar or ID number"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchById()}
                  className="flex-1"
                />
                <Button
                  data-testid="search-staff-button"
                  onClick={handleSearchById}
                  disabled={searching}
                  className="bg-gk-teal hover:bg-gk-teal-hover"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {searching ? 'Searching...' : 'Search'}
                </Button>
              </div>
            </div>
          )}

          {/* Search by Villa */}
          {searchMode === 'villa' && (
            <div className="space-y-2">
              <Label htmlFor="villa-search">Villa Number</Label>
              <div className="flex gap-2">
                <Input
                  id="villa-search"
                  data-testid="villa-search-input"
                  placeholder="Enter villa number (e.g., A-101)"
                  value={villaSearch}
                  onChange={(e) => setVillaSearch(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchByVilla()}
                  className="flex-1"
                />
                <Button
                  data-testid="search-villa-staff-button"
                  onClick={handleSearchByVilla}
                  disabled={searching}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  {searching ? 'Searching...' : 'Find Staff'}
                </Button>
              </div>
            </div>
          )}

          {/* Villa Staff List */}
          {villaStaffList.length > 0 && !searchedStaff && (
            <div className="p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200">
              <div className="flex items-center gap-2 mb-3">
                <Home className="w-5 h-5 text-emerald-600" />
                <h4 className="font-bold text-emerald-900">Staff for Villa {villaSearch}</h4>
                <Badge className="bg-emerald-600">{villaStaffList.length}</Badge>
              </div>
              <div className="space-y-2">
                {villaStaffList.map((staff) => (
                  <div
                    key={staff.id}
                    className="p-3 bg-white rounded-lg border flex items-center justify-between hover:border-emerald-400 cursor-pointer transition-all"
                    onClick={() => handleSelectStaffFromVilla(staff)}
                  >
                    <div>
                      <p className="font-semibold">{staff.name}</p>
                      <p className="text-xs text-slate-600">{staff.job_description}</p>
                      <p className="text-xs text-slate-500">ID: {staff.id_number}</p>
                    </div>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                      Select for Check-in
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Staff Found Card */}
          {searchedStaff && (
            <div className="p-4 bg-emerald-50 rounded-lg border-2 border-emerald-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                  <h4 className="font-bold text-lg text-emerald-900">Staff Found</h4>
                </div>
                <Button size="sm" variant="outline" onClick={clearSearch} className="text-slate-600">Clear</Button>
              </div>
              <div className="space-y-2 mb-4">
                <p className="text-sm"><strong>Name:</strong> {searchedStaff.name}</p>
                <p className="text-sm"><strong>ID:</strong> {searchedStaff.id_number}</p>
                <p className="text-sm"><strong>Job:</strong> {searchedStaff.job_description}</p>
                <p className="text-sm"><strong>Phone:</strong> {searchedStaff.phone}</p>
                <p className="text-sm">
                  <strong>Assigned Villas:</strong>{' '}
                  {searchedStaff.assigned_villas?.map((v) => (
                    <Badge key={v} className="bg-slate-600 mr-1">{v}</Badge>
                  ))}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="villa-select">Select Villa for Check-in</Label>
                <select
                  id="villa-select"
                  data-testid="villa-select"
                  value={selectedVilla}
                  onChange={(e) => setSelectedVilla(e.target.value)}
                  className="w-full p-2 border rounded-lg"
                >
                  <option value="">Choose villa</option>
                  {searchedStaff.assigned_villas?.map((villa) => (
                    <option key={villa} value={villa}>{villa}</option>
                  ))}
                </select>
              </div>
              <Button
                data-testid="checkin-staff-button"
                onClick={handleCheckIn}
                className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                Check In Staff
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
